import React, {useEffect, useMemo, useState} from 'react';
import 'moment/locale/pt-br';
import MomentUtils from '@date-io/moment';
import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import {makeStyles} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import {GetAppOutlined, PublishOutlined, SyncOutlined} from '@material-ui/icons';
import PageWrapper from '../../../shared/pagewrapper/PageWrapper.js';
import PageTitle from '../../../shared/pagetitle/PageTitle.js';
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import OutlinedButton from '../../../shared/buttons/OutlinedButton.tsx';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import useConfirmationDialog from '../../../shared/confirmationdialog/useConfirmationDialog.js';
import {useToast} from '../../../shared/toast/ToastProvider.tsx';
import PageNaoEncontrada from '../../../PageNaoEncontrada.jsx';
import SeletorArquivoXlsx from '../../components/cargasaf/SeletorArquivoXlsx.jsx';
import {CampoChamada, CampoDataChamada, CampoProcessoSeletivo} from '../../components/cargasaf/CamposCargaSaf.jsx';
import {
    CHAMADAS_PROUNI,
    CONFIRMACAO_CARREGAR_PROUNI,
    comChave,
    lerErroDeArquivo,
    MENSAGEM_PROUNI_CONSOLIDADO,
    montarPayloadExcelProuni,
    montarPayloadProuni,
    podeConsolidarProuni,
    resumoCargaProuni,
    SEM_REGISTROS_PROUNI
} from '../../components/cargasaf/cargaSaf.js';
import {ouTraco} from '../../components/inscricao/documentoInscricao.js';
import useCarregamentoPagina from '../../components/useCarregamentoPagina.js';
import {PAGINACAO_PADRAO} from '../../components/paginacao.js';
import {salvarArquivoResposta} from '../../../../utils/download.js';
import useCheckModule from '../../../../hooks/useCheckModule.js';
import useFetch from '../../../../hooks/useFetch.js';
import useCreate from '../../../../hooks/useCreate.js';
import useCreateFile from '../../../../hooks/useCreateFile.js';
import {
    URL_CARGAS_SAF_APOIO_PROCESSOS_SELECAO,
    URL_CARGAS_SAF_PROUNI_CARGA,
    URL_CARGAS_SAF_PROUNI_CONSOLIDACAO,
    URL_CARGAS_SAF_PROUNI_EXCEL
} from '../../../../constants/index.js';
import {containerPaper} from '../../../../utils/styles.js';

const PARAMS_VAZIOS = {};
const FILTER_OPTIONS = {filterByColumn: true};
const TABLE_CONTAINER_PROPS = {component: 'div'};
const NOME_EXCEL = 'planilha_prouni_consulta_resultado.xls';

const useStyles = makeStyles(theme => ({
    ...containerPaper(theme),
    acoes: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        alignItems: 'center'
    }
}));

/*
 * Carga Prouni/Fies (módulo PROUNI): o usuário escolhe o processo seletivo, a data e a chamada e
 * envia a planilha de resultado (e, se houver, a de consulta de candidatos). "Consolidar Planilha"
 * junta as duas e mostra a tabela; "Carregar Aprovados no SAF" grava os candidatos (que passam a
 * valer para a inscrição) e atualiza Estado/Observação; "Baixar Excel" gera a planilha com as
 * linhas da tabela. Mudar qualquer campo ou arquivo depois de consolidar limpa a tabela.
 */
export default function PageCargaProuni() {
    const classes = useStyles();
    const checkModule = useCheckModule();
    const podeAcessar = checkModule('PROUNI');
    const confirmation = useConfirmationDialog();
    const {createToast} = useToast();
    const [fetchApoio, carregandoApoio] = useFetch({returnInnerLoadingState: true});
    const [consolidar, consolidando] = useCreate({returnInnerLoadingState: true});
    const [carregar, carregando] = useCreate({returnInnerLoadingState: true});
    const [baixarExcel, baixando] = useCreateFile({returnInnerLoadingState: true});

    const [processos, setProcessos] = useState([]);
    const [idProcesso, setIdProcesso] = useState('');
    const [dataChamada, setDataChamada] = useState(null);
    const [tipoChamada, setTipoChamada] = useState('');
    const [resultado, setResultado] = useState(null);
    const [candidatos, setCandidatos] = useState(null);
    const [linhas, setLinhas] = useState([]);

    useEffect(() => {
        if (podeAcessar) {
            fetchApoio(URL_CARGAS_SAF_APOIO_PROCESSOS_SELECAO, PARAMS_VAZIOS,
                dados => setProcessos(Array.isArray(dados) ? dados : []));
        }
    }, [podeAcessar]);

    const linhasComChave = useMemo(() => comChave(linhas), [linhas]);

    // Qualquer mudança nos dados da carga invalida a consolidação: é preciso consolidar de novo.
    const alterar = setter => valor => {
        setter(valor);
        setLinhas([]);
    };

    const trocarProcesso = valor => {
        setIdProcesso(valor);
        setDataChamada(null);
        setTipoChamada('');
        setResultado(null);
        setCandidatos(null);
        setLinhas([]);
    };

    const dados = {idProcessoSeletivo: idProcesso, dataChamada, tipoChamada, resultado, candidatos};

    const consolidarPlanilha = () => consolidar(URL_CARGAS_SAF_PROUNI_CONSOLIDACAO, montarPayloadProuni(dados), resposta => {
        setLinhas(Array.isArray(resposta?.linhas) ? resposta.linhas : []);
        createToast({severity: 'success', message: MENSAGEM_PROUNI_CONSOLIDADO});
    });

    const carregarAprovados = () => confirmation(CONFIRMACAO_CARREGAR_PROUNI, () =>
        carregar(URL_CARGAS_SAF_PROUNI_CARGA, montarPayloadProuni(dados), resposta => {
            setLinhas(Array.isArray(resposta?.linhas) ? resposta.linhas : []);
            createToast(resumoCargaProuni(resposta));
        }));

    const baixar = () => baixarExcel(URL_CARGAS_SAF_PROUNI_EXCEL, montarPayloadExcelProuni(linhas),
        response => salvarArquivoResposta(response, NOME_EXCEL),
        (erro, tratarErro) => lerErroDeArquivo(erro).then(tratarErro));

    // Loading da página pelo LoadingModal global (o BlockUI fica só nas tabelas).
    useCarregamentoPagina(carregandoApoio || consolidando || carregando || baixando);

    if (!podeAcessar) {
        return <PageNaoEncontrada/>;
    }

    const ocupado = carregandoApoio || consolidando || carregando || baixando;
    const temTabela = linhas.length > 0;

    return (
        <PageWrapper>
            <PageTitle title='Carga Prouni/Fies'/>
            <Paper className={classes.containerPaper}>
                <MuiPickersUtilsProvider utils={MomentUtils} locale='pt-br'>
                    <Grid container spacing={3}>
                        <CampoProcessoSeletivo valor={idProcesso} opcoes={processos} onChange={trocarProcesso}/>
                        <CampoDataChamada valor={dataChamada} onChange={alterar(setDataChamada)}/>
                        <CampoChamada valor={tipoChamada} opcoes={CHAMADAS_PROUNI} onChange={alterar(setTipoChamada)}/>
                        <Grid item xs={12} sm={6}>
                            <SeletorArquivoXlsx
                                id='arquivoResultadoProuni'
                                rotulo='Resultado Prouni (xlsx) *'
                                arquivo={resultado}
                                onSelecionar={alterar(setResultado)}
                                onRemover={() => alterar(setResultado)(null)}
                                disabled={ocupado}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SeletorArquivoXlsx
                                id='arquivoCandidatosProuni'
                                rotulo='Consulta Candidatos Prouni (xlsx)'
                                arquivo={candidatos}
                                onSelecionar={alterar(setCandidatos)}
                                onRemover={() => alterar(setCandidatos)(null)}
                                disabled={ocupado}/>
                        </Grid>
                        <Grid item xs={12}>
                            <div className={classes.acoes}>
                                <ContainedButton
                                    startIcon={<SyncOutlined/>}
                                    disabled={ocupado || !podeConsolidarProuni(dados)}
                                    onClick={consolidarPlanilha}>
                                    Consolidar Planilha
                                </ContainedButton>
                                <ContainedButton
                                    startIcon={<PublishOutlined/>}
                                    disabled={ocupado || !temTabela}
                                    onClick={carregarAprovados}>
                                    Carregar Aprovados no SAF
                                </ContainedButton>
                                <OutlinedButton startIcon={<GetAppOutlined/>} disabled={ocupado || !temTabela} onClick={baixar}>
                                    Baixar Excel
                                </OutlinedButton>
                            </div>
                        </Grid>
                        <Grid item xs={12}>
                            <LabelValue label='Candidatos consolidados'>
                                <DataGrid
                                    rows={linhasComChave}
                                    comparatorKeys={['chave']}
                                    filterOptions={FILTER_OPTIONS}
                                    paginate
                                    paginateOptions={PAGINACAO_PADRAO}
                                    tableContainerProps={TABLE_CONTAINER_PROPS}
                                    noRecordsText={SEM_REGISTROS_PROUNI}
                                    columns={[
                                        {label: 'Num. Proc.', field: 'numeroProcesso', filterable: true, valueGetter: valor => ouTraco(valor)},
                                        {label: 'Num. Curso (MEC)', field: 'numeroCursoMec', filterable: false, valueGetter: valor => ouTraco(valor)},
                                        {label: 'Curso', field: 'nomeInternoCurso', filterable: true, valueGetter: valor => ouTraco(valor)},
                                        {label: 'Turno', field: 'turno', filterable: false, valueGetter: valor => ouTraco(valor)},
                                        {label: 'Nome', field: 'nome', filterable: true, valueGetter: valor => ouTraco(valor)},
                                        {label: 'Situação', field: 'situacao', filterable: true, valueGetter: valor => ouTraco(valor)},
                                        {label: 'Estado', field: 'estado', filterable: true, valueGetter: valor => ouTraco(valor)},
                                        {label: 'Observação', field: 'observacao', filterable: false, valueGetter: valor => ouTraco(valor)}
                                    ]}/>
                            </LabelValue>
                        </Grid>
                    </Grid>
                </MuiPickersUtilsProvider>
            </Paper>
        </PageWrapper>
    );
}
